import { buildProductConfigSnapshot, normalizeProductConfig, resolveProductMinSqft } from "./productConfigs";

export const EDITABLE_PRODUCT_RATE_FIELDS = ["sqftRate", "ratePerSqft", "ratePerLength", "ratePerFeet", "unitRate", "rate", "manualAmount", "mrp"];
const RATE_ALIASES = ["sqftRate", "ratePerSqft", "ratePerLength", "ratePerFeet", "runningFeetRate", "unitRate", "ratePerPiece", "pieceRate", "rate", "rateAmount", "manualAmount"];

export function resolveDefaultRateForProduct(productConfig = null, row = {}) {
  const config = normalizeProductConfig(productConfig || row.productConfigSnapshot || {});
  switch (config.calculationType) {
    case "sqft_only":
    case "sqft_plus_fabric": return { field: "sqftRate", rate: config.sqftRules.defaultRate, unit: "sqft" };
    case "quantity_rate": return { field: "unitRate", rate: config.quantityRules.defaultRate, unit: config.quantityRules.defaultUnit };
    case "running_length_rate": return { field: "ratePerLength", rate: config.runningFeetRules.defaultRate, unit: config.runningFeetRules.defaultUnit };
    case "manual_amount": return { field: "manualAmount", rate: "", unit: "amount" };
    default: return { field: "mrp", rate: null, unit: "m" };
  }
}

export function applyProductToRow(row = {}, productConfig, { resetRate = false, legacySettings = {} } = {}) {
  const config = normalizeProductConfig(productConfig);
  const target = resolveDefaultRateForProduct(config);
  const minimumSqft = resolveProductMinSqft({}, config, legacySettings);
  const previous = resolveDefaultRateForProduct(row.productConfigSnapshot || { name: row.productName || row.productType, calculationType: row.calculationType }, row);
  const oldField = row.rateField || previous.field;
  // Legacy touched flags record editing history, not an intentional rate override.
  const manual = row.rateSources?.[oldField] === "manual_override" ||
    ((row.rateSource === "manual_override" || row.rateOverridden === true) && (!row.rateField || row.rateField === oldField));
  const preserveManual = !resetRate && ((manual && oldField === target.field) || (target.field === "mrp" && row.rateSources?.mrp === "manual_override"));
  const next = {
    ...row,
    productId: config.id, productConfigId: config.id, productName: config.name,
    product: config.name, productType: config.name, category: config.category,
    calculationType: config.calculationType, productConfigSnapshot: buildProductConfigSnapshot(config),
    unit: target.unit, lengthUnit: config.runningFeetRules.defaultUnit,
    minSqft: minimumSqft, minimumSqft,
    minBillableSqft: minimumSqft, minimumBillableSqft: minimumSqft,
    sqftRules: { ...config.sqftRules }, discountScope: config.discountScope,
    rateField: target.field,
    rateSource: preserveManual ? "manual_override" : target.field === "mrp" && (row.rateSource === "catalogue" || row.rateSources?.mrp === "catalogue") ? "catalogue" : "product_default",
    rateOverridden: preserveManual,
    rateChangeNotice: !preserveManual && (manual || resetRate) ? target.rate != null ? "Rate updated from selected product. You can edit it manually." : "Product pricing updated. Fabric price is entered separately." : "",
    rateSources: { ...(row.rateSources || {}) },
    cataloguePricing: null,
    catalogueRates: ["fabric_meter", "sqft_plus_fabric"].includes(config.calculationType) && row.catalogueRates?.mrp
      ? { mrp: row.catalogueRates.mrp } : {},
    catalogueRateField: "",
    cataloguePriceUnit: "",
    cataloguePricingError: "",
    touchedFields: { ...(row.touchedFields || {}) },
  };
  RATE_ALIASES.forEach((field) => { next[field] = ""; next.touchedFields[field] = false; delete next.rateSources[field]; });
  if (!["fabric_meter", "sqft_plus_fabric"].includes(config.calculationType)) {
    if (next.rateSources.mrp === "catalogue") delete next.rateSources.mrp;
    if (["sr_price_rule", "catalogue_default", "needs_sr_number", "unmatched"].includes(next.rrpSource)) next.rrpSource = "not_applicable";
  }
  // Fabric pricing belongs to the selected fabric, rather than a blind's area rate.
  next[target.field] = preserveManual ? row[target.field] ?? row[oldField] ?? "" : target.rate ?? row[target.field] ?? "";
  if (target.field === "sqftRate") next.ratePerSqft = next.sqftRate;
  if (target.field === "ratePerLength") next.ratePerFeet = next.ratePerLength;
  const configuredGst = productConfig?.gstRate ?? productConfig?.defaultGstRate ?? productConfig?.taxRules?.gstRate;
  if (configuredGst !== "" && configuredGst != null && Number.isFinite(Number(configuredGst)) && Number(configuredGst) >= 0 && Number(configuredGst) <= 100) {
    next.gstRate = Number(configuredGst);
    next.gst = next.gstRate;
    next.gstSource = "product_default";
  } else if (row.gstSource === "product_default") {
    next.gstRate = "";
    next.gst = "";
    next.gstSource = "quote_default";
  }
  next.touchedFields[target.field] = preserveManual;
  next.rateSources[target.field] = next.rateSource;
  return next;
}
